import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { firestoreService } from '@/services/firestoreService';
import { useAuth } from '@/contexts/AuthContext';

interface Prompt {
  id: string;
  name: string;
  type: 'transcript' | 'filing' | 'copilot' | 'other';
  content: string;
  description?: string;
  version?: number;
  updatedAt?: any;
  updatedBy?: string;
}

const DEFAULT_PROMPTS: Omit<Prompt, 'id' | 'updatedAt' | 'updatedBy'>[] = [
  {
    name: 'Transcript Analysis',
    type: 'transcript',
    description: 'Main prompt for analyzing earnings call transcripts',
    content: `You are a financial analyst AI. Analyze the following earnings call transcript and provide a comprehensive analysis in JSON format.

Return a JSON object with the following structure:
{
  "summary": "A comprehensive summary of the earnings call (2-3 paragraphs)",
  "risksAndOpportunities": {
    "risks": ["risk 1", "risk 2", ...],
    "opportunities": ["opportunity 1", "opportunity 2", ...]
  },
  "keyNumbers": {
    "revenue": "revenue figure and context",
    "earnings": "earnings figure and context",
    "guidance": "guidance provided",
    "other": {"metric": "value", ...}
  },
  "strategyAndInitiatives": ["strategy 1", "strategy 2", ...],
  "sentiment": {
    "overall": "positive" | "neutral" | "negative",
    "score": 0-100,
    "indicators": ["indicator 1", "indicator 2", ...]
  },
  "analystQA": {
    "questions": [
      {
        "question": "analyst question",
        "answer": "company answer",
        "analyst": "analyst name if mentioned"
      }
    ]
  },
  "radarChart": {
    "strategicPlanning": 0-100,
    "organizationalLeadership": 0-100,
    "communicationSkills": 0-100,
    "externalAdaptability": 0-100,
    "innovationCapability": 0-100
  },
  "mindmap": {
    "nodes": [
      {"id": "center", "label": "Company Name - Quarter", "type": "center"},
      {"id": "summary", "label": "Summary", "type": "category"},
      {"id": "revenue", "label": "Revenue: [amount]", "type": "metric"},
      {"id": "risk1", "label": "[Risk 1]", "type": "risk"},
      {"id": "opp1", "label": "[Opportunity 1]", "type": "opportunity"},
      {"id": "strategy1", "label": "[Strategy 1]", "type": "strategy"}
    ],
    "edges": [
      {"from": "center", "to": "summary", "label": ""},
      {"from": "center", "to": "revenue", "label": ""},
      {"from": "summary", "to": "risk1", "label": ""},
      {"from": "summary", "to": "opp1", "label": ""},
      {"from": "center", "to": "strategy1", "label": ""}
    ]
  }
}

For the radarChart scores (0-100 each):
- strategicPlanning: Evaluate clarity and quality of strategic vision, long-term planning, and strategic initiatives mentioned
- organizationalLeadership: Assess leadership effectiveness, team execution, organizational structure, and management quality
- communicationSkills: Rate clarity of communication, transparency, ability to answer questions, and overall presentation
- externalAdaptability: Measure responsiveness to market changes, competitive positioning, and ability to adapt to external factors
- innovationCapability: Evaluate innovation initiatives, R&D focus, product development, and technological advancement

For the mindmap:
- Create a central node with company name and quarter
- Add category nodes for: Summary, Financials, Risks, Opportunities, Strategies, Sentiment
- Add specific metric/risk/opportunity/strategy nodes as children
- Connect nodes logically (center → categories → specifics)
- Use types: "center", "category", "metric", "risk", "opportunity", "strategy", "sentiment"
- Keep node labels concise (max 30 characters)
- Create 8-15 nodes total for a meaningful visualization

Be thorough and extract all important information.`,
  },
  {
    name: 'Filing Analysis',
    type: 'filing',
    description: 'Main prompt for analyzing financial filings (10-K, 10-Q)',
    content: `You are a financial analyst AI. Analyze the following financial filing (10-K, 10-Q, or annual report) and provide a comprehensive analysis in JSON format.

Return a JSON object with the following structure:
{
  "summary": "A comprehensive summary of the filing (3-4 paragraphs)",
  "keyMetrics": {
    "metric1": "value and context",
    "metric2": "value and context",
    ...
  },
  "segmentBreakdown": [
    {
      "segment": "segment name",
      "revenue": "revenue figure",
      "growth": "growth percentage",
      "notes": "key notes about this segment"
    }
  ],
  "litigationAndRisks": {
    "litigation": ["litigation item 1", "litigation item 2", ...],
    "risks": ["risk 1", "risk 2", ...]
  },
  "forwardLookingStatements": ["statement 1", "statement 2", ...],
  "yearOverYearChanges": [
    {
      "metric": "metric name",
      "current": "current period value",
      "previous": "previous period value",
      "change": "absolute change",
      "percentage": "percentage change"
    }
  ]
}

Be thorough and extract all important financial information.`,
  },
  {
    name: 'Copilot System Prompt',
    type: 'copilot',
    description: 'System prompt for Investor Copilot chat interface',
    content: `You are an expert financial analyst AI assistant (Investor Copilot) for the Blazerex platform. Your role is to provide deep, actionable financial insights based on earnings calls, financial reports, and company data.

CRITICAL INSTRUCTIONS:
- You ARE an AI assistant WITH ACCESS to earnings call analyses, financial report analyses, and company data through the Blazerex platform
- When context data is provided below (marked with === sections), you MUST use that data to answer questions
- NEVER say "I don't have access" or "I can't access" - you have access through the provided context
- The context below contains REAL earnings call analyses, financial metrics, and company data from the platform
- Use the provided context extensively - extract all relevant numbers, insights, and information from it
- When the user asks about "latest" or "most recent" earnings, use the data provided below - it IS the latest earnings call data
- Only say data is unavailable if NO context is provided for that specific question

CAPABILITIES:
- Analyze earnings call transcripts and extract key insights from provided analyses
- Summarize quarterly results with financial metrics from the platform's data
- Create bull and bear investment cases based on provided earnings/filing analyses
- Compare guidance and performance quarter-over-quarter (QoQ) and year-over-year (YoY) using provided data
- Identify risks, opportunities, and strategic initiatives from the analyses
- Analyze sentiment and management tone from the provided context
- Extract and explain key financial numbers from the platform's analyses

RESPONSE GUIDELINES:
1. ALWAYS use the provided context: When context data is provided below, you MUST use it to answer questions. The context contains real earnings call analyses, financial metrics, and company data from the Blazerex platform.
2. Never say you don't have access: If context is provided, you have access to that data. Only say data is unavailable if NO context is provided for that specific question.
3. Always be specific: Include actual numbers, percentages, and metrics from the provided context
4. Structure your answers clearly: 
   - Use bullet points for lists
   - Use headers (##) for major sections
   - Use numbered lists for step-by-step explanations
   - Use bold text (**text**) for emphasis on key numbers
5. Provide context: Explain what the numbers mean and why they matter
6. Be actionable: Give insights that help with investment decisions
7. Cite sources: When referencing data, mention it's from "the platform's analysis" or "the earnings call analysis"
8. For comparisons: Highlight changes, trends, and implications with specific numbers from the context
9. For bull/bear cases: Present balanced, evidence-based arguments with supporting data from the provided analyses
10. Use context extensively: Extract all relevant information from the provided context - don't make up numbers
11. If no context is provided for a specific question: Say "I don't have access to that specific data in the current context. Please provide more context or select a specific earnings call/filing."
12. Format numbers clearly: Use commas for thousands, include currency symbols, show percentages

Be thorough, professional, and insightful. Your answers should help investors make informed decisions.`,
  },
];

export const PromptManagement: React.FC = () => {
  const { user } = useAuth();
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    try {
      setLoading(true);
      const existingPrompts = await firestoreService.getDocuments<Prompt>('prompts', []);
      
      // If no prompts exist, initialize with defaults
      if (existingPrompts.length === 0) {
        // Create default prompts
        const defaultPrompts = await Promise.all(
          DEFAULT_PROMPTS.map(async (defaultPrompt) => {
            const newPrompt: Omit<Prompt, 'id'> = {
              ...defaultPrompt,
              version: 1,
              updatedAt: new Date(),
              updatedBy: user?.uid || 'system',
            };
            const docId = await firestoreService.createDocument('prompts', newPrompt);
            return { id: docId, ...newPrompt };
          })
        );
        setPrompts(defaultPrompts);
      } else {
        setPrompts(existingPrompts);
      }
    } catch (error) {
      console.error('Failed to load prompts:', error);
      setPrompts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (prompt: Prompt) => {
    setEditingPrompt(prompt);
    setEditContent(prompt.content);
  };

  const handleSave = async () => {
    if (!editingPrompt || !user) return;

    try {
      setSaving(true);
      const updatedPrompt: Partial<Prompt> = {
        content: editContent,
        version: (editingPrompt.version || 1) + 1,
        updatedAt: new Date(),
        updatedBy: user.uid,
      };

      await firestoreService.updateDocument('prompts', editingPrompt.id, updatedPrompt);
      
      // Update local state
      setPrompts(prompts.map(p => 
        p.id === editingPrompt.id 
          ? { ...p, ...updatedPrompt }
          : p
      ));
      
      setEditingPrompt(null);
      setEditContent('');
      alert('Prompt updated successfully! Note: Cloud Functions need to be updated to use Firestore prompts.');
    } catch (error) {
      console.error('Failed to save prompt:', error);
      alert('Failed to save prompt. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingPrompt(null);
    setEditContent('');
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'transcript':
        return 'bg-blue-100 text-blue-800';
      case 'filing':
        return 'bg-green-100 text-green-800';
      case 'copilot':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blazerex-green"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="sub-header text-2xl mb-2">Prompt Management</h2>
          <p className="body-text text-gray-600">
            Edit and manage ChatGPT prompts used for analysis. Changes will affect future analyses.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={loadPrompts}
        >
          Refresh
        </Button>
      </div>

      {editingPrompt ? (
        <Card>
          <div className="mb-4">
            <h3 className="sub-header text-xl mb-2">Editing: {editingPrompt.name}</h3>
            <p className="body-text text-sm text-gray-600 mb-4">
              {editingPrompt.description}
            </p>
            <div className="flex items-center gap-2 mb-4">
              <span className={`px-2 py-1 rounded text-xs ${getTypeColor(editingPrompt.type)}`}>
                {editingPrompt.type}
              </span>
              {editingPrompt.version && (
                <span className="body-text text-xs text-gray-500">
                  Version {editingPrompt.version}
                </span>
              )}
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block body-text text-sm font-medium text-gray-700 mb-2">
              Prompt Content
            </label>
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full h-96 p-4 border border-gray-300 rounded-lg font-mono text-sm"
              placeholder="Enter prompt content..."
            />
            <p className="body-text text-xs text-gray-500 mt-2">
              {editContent.length} characters
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving || !editContent.trim()}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>

          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="body-text text-sm text-yellow-800">
              <strong>Note:</strong> After saving, you'll need to update Cloud Functions to read prompts from Firestore 
              instead of hardcoded values. Currently, prompts are hardcoded in the function code.
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {prompts.map((prompt) => (
            <Card key={prompt.id} hover>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="sub-header text-lg">{prompt.name}</h3>
                    <span className={`px-2 py-1 rounded text-xs ${getTypeColor(prompt.type)}`}>
                      {prompt.type}
                    </span>
                    {prompt.version && (
                      <span className="body-text text-xs text-gray-500">
                        v{prompt.version}
                      </span>
                    )}
                  </div>
                  {prompt.description && (
                    <p className="body-text text-sm text-gray-600 mb-3">
                      {prompt.description}
                    </p>
                  )}
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>
                      {prompt.content.length} characters
                    </span>
                    {prompt.updatedAt && (
                      <span>
                        Updated: {new Date(prompt.updatedAt?.toDate?.() || prompt.updatedAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <div className="mt-3 p-3 bg-gray-50 rounded border border-gray-200">
                    <p className="body-text text-xs text-gray-600 font-mono line-clamp-3">
                      {prompt.content.substring(0, 200)}...
                    </p>
                  </div>
                </div>
                <div className="ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(prompt)}
                  >
                    Edit
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {prompts.length === 0 && !loading && (
        <Card>
          <div className="text-center py-12">
            <p className="body-text text-gray-600 mb-4">No prompts found</p>
            <Button variant="outline" onClick={loadPrompts}>
              Initialize Default Prompts
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};
